import type { ReactNode } from "react";
import {
  Box,
  ButtonGroup,
  Container,
  Flex,
  Spacer,
} from "@chakra-ui/react";
import { BreadcrumbJsonLd, NextSeo } from "next-seo";
import CopyLinkButton from "components/common/copy-link-button";
import EntityIcons from "components/icons/entity-icons";
import CreateCitationButton from "components/profile/citation/create-citation-button";
import DownloadPdfButton from "components/profile/download-pdf-modal/download-pdf-button";
import EditProfileButton from "components/profile/edit-profile-button";
import {
  ProfileHeader,
  ProfileHeaderName,
  ProfileHeaderStat,
  ProfileHeaderStats,
} from "components/profile/profile-header";
import ProfileLogo from "components/profile/profile-logo";
import RequestUpdateButton from "components/profile/update-modals/request-update-button";
import { RequestUpdateProvider } from "components/profile/update-modals/request-update-modal";
import SuggestUpdateButton from "components/profile/update-modals/suggest-update-button";
import usePermission from "hooks/use-permission";
import useUser from "hooks/use-user";
import colorSchemes from "data/color-schemes";
import type { InvestorInitialDataResponse } from "types/api/investor";
import ActiveTab from "../../company/company-page-layout/active-tab";
import TabHover from "../../company/company-page-layout/tab-hover";

export interface InvestorPageLayoutProps {
  children: ReactNode;
  investor: InvestorInitialDataResponse;
  slug: string;
}

const InvestorPageLayout = ({
  children,
  investor: {
    name = "",
    city = "",
    stateOrProvince = "",
    country = "",
    id: investorId,
    urlSlug = "",
  } = {} as InvestorInitialDataResponse,
  slug,
}: InvestorPageLayoutProps) => {
  const user = useUser();
  const isPublic = !user?.isLoggedIn || false;

  const hasInvestorContactsPermission = usePermission("investorContacts");

  const hqLocation = [city, stateOrProvince, country]
    .filter(Boolean)
    .slice(0, 2)
    .join(", ");

  const pageTitle = `${name} - Investor Profile: Portfolio, Deals, and Investment`;
  const canonicalUrl = `https://system.privco.com/investor/${slug}`;

  const ogImageParams = new URLSearchParams({
    type: "investor",
    name,
  });
  const ogImageUrl = `/api/og?${ogImageParams.toString()}`;

  return (
    <>
      <NextSeo
        title={pageTitle}
        canonical={canonicalUrl}
        openGraph={{
          title: pageTitle,
          url: canonicalUrl,
          images: [
            {
              url: ogImageUrl,
              alt: name,
            },
          ],
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name,
            item: canonicalUrl,
          },
        ]}
      />

      <RequestUpdateProvider
        resource="investor"
        identifier={{ name, id: investorId, slug }}
      >
        <Flex bg="white" direction="column" h="100%" overflowY="auto">
          <ProfileHeader>
            <ProfileLogo
              name={name}
              colorScheme={colorSchemes.investor}
              fallbackIcon={EntityIcons.investor}
            />

            <ProfileHeaderName name={name}>
              {isPublic ? (
                <SuggestUpdateButton
                  identifier={{ name, id: investorId, slug }}
                  type="investor"
                />
              ) : (
                <RequestUpdateButton />
              )}

              <CreateCitationButton name={name} slug={slug} type="investor" />

              <CopyLinkButton
                label="Copy Profile Link"
                variant="solid"
                data-cy="copy-investor-profile-link"
              />

              <EditProfileButton
                type="investor"
                profileId={investorId}
                data-cy="edit-invesor-button"
              />
            </ProfileHeaderName>

            <ProfileHeaderStats>
              <ProfileHeaderStat label="Headquarters">
                {hqLocation}
              </ProfileHeaderStat>

              <ProfileHeaderStat label="Classification" color="green.600">
                Investor
              </ProfileHeaderStat>
            </ProfileHeaderStats>
          </ProfileHeader>

          <Box borderBottomWidth={1}>
            <Container maxW="8xl">
            <Flex
                  aria-label="Company Profile Navigation"
                  as="nav"
                  align="center"
                  overflowX="auto"
                  overflowY="visible"
                  mx={-4}
                  px={4}
                  py={1}
                  my={-1}
                >
                <TabHover>
                  <ActiveTab slug={`/investor/${urlSlug}`}>Overview</ActiveTab>
                  {hasInvestorContactsPermission && (
                      <ActiveTab slug={`/investor/details/${urlSlug}/contacts`}>
                        Contacts
                      </ActiveTab>
                    )}
                </TabHover>
              
                <Spacer />

                <ButtonGroup size="sm" variant="outline" colorScheme="blue">
                  <DownloadPdfButton
                    id={investorId}
                    slug={slug}
                    type="investor"
                    keepFullWidth
                  />
                </ButtonGroup>
                </Flex>
            </Container>
          </Box>

          {children}
        </Flex>
      </RequestUpdateProvider>
    </>
  );
};

export default InvestorPageLayout;